/*
 * The renderer registry. `renderWidget` dispatches on `widget.kind` and picks
 * the concrete Preact component. Everything upstream — layout containers,
 * preview pages, the eventual project loader — imports only this dispatcher.
 *
 * When a new widget component lands, add a case here AND add the JSON schema
 * counterpart in HVACloud.Core.Hmi.Schema. A runtime warning fires for kinds
 * without a registered renderer so a partially-migrated build shows something
 * useful instead of a blank tile.
 */

import type { WidgetDefinition } from '../widgets';
import type { IPointBindingSource } from '../binding';
// Command family
import { MomentaryButtonRenderer } from './MomentaryButtonRenderer';
import { ToggleRenderer } from './ToggleRenderer';
import { EnumSetterRenderer } from './EnumSetterRenderer';
import { SetpointStepperRenderer } from './SetpointStepperRenderer';
import { SliderRenderer } from './SliderRenderer';
import { LockGuardRenderer } from './LockGuardRenderer';
// Display family
import { GaugeRenderer } from './GaugeRenderer';
import { RollingCounterRenderer } from './RollingCounterRenderer';
import { StatusLedRenderer } from './StatusLedRenderer';
import { ValueTileRenderer } from './ValueTileRenderer';
import { AlarmIndicatorRenderer } from './AlarmIndicatorRenderer';
import { SparklineRenderer } from './SparklineRenderer';
import { TrendRenderer } from './TrendRenderer';
// HVAC family
import { SchematicOverlayRenderer } from './SchematicOverlayRenderer';
import { EnergyFlowRenderer } from './EnergyFlowRenderer';
import { ScheduleEditorRenderer } from './ScheduleEditorRenderer';
import { StageCascadeRenderer } from './StageCascadeRenderer';
// Compound family
import { HeatCircuitTileRenderer } from './HeatCircuitTileRenderer';
import { HeatPumpKpiRenderer } from './HeatPumpKpiRenderer';
import { HotWaterTileRenderer } from './HotWaterTileRenderer';
import { RoomThermostatRenderer } from './RoomThermostatRenderer';
import { PvSelfConsumptionRenderer } from './PvSelfConsumptionRenderer';
import { WeatherTileRenderer } from './WeatherTileRenderer';
// Free-design family (docs/hmi-panel-ausbau.md §6)
import {
  ActionButtonRenderer, HeroValueRenderer, ImageRenderer, NavBarRenderer, ShapeRenderer, ThemeToggleRenderer, AlarmListRenderer, SymbolRenderer, TextRenderer,
} from '../panel/PanelWidgets';
import { DayScheduleRenderer, DialRenderer, HeatingCurveRenderer, TankRenderer } from '../panel/RichWidgets';
import { MonthBarsRenderer, PowerFlowRenderer, SplitBarRenderer } from '../panel/EnergyWidgets';
import { UserInviteRenderer } from '../panel/UserInviteWidget';
import { SchematicPartRenderer } from '../parts/SchematicPartRenderer';

interface RenderProps {
  widget: WidgetDefinition;
  source: IPointBindingSource;
}

export function renderWidget({ widget, source }: RenderProps) {
  switch (widget.kind) {
    // Command
    case 'momentary-button':
      return <MomentaryButtonRenderer widget={widget} source={source} />;
    case 'toggle':
      return <ToggleRenderer widget={widget} source={source} />;
    case 'enum-setter':
      return <EnumSetterRenderer widget={widget} source={source} />;
    case 'setpoint-stepper':
      return <SetpointStepperRenderer widget={widget} source={source} />;
    case 'slider':
      return <SliderRenderer widget={widget} source={source} />;
    case 'lock-guard':
      return <LockGuardRenderer widget={widget} source={source} />;
    // Display
    case 'gauge':
      return <GaugeRenderer widget={widget} source={source} />;
    case 'rolling-counter':
      return <RollingCounterRenderer widget={widget} source={source} />;
    case 'status-led':
      return <StatusLedRenderer widget={widget} source={source} />;
    case 'value-tile':
      return <ValueTileRenderer widget={widget} source={source} />;
    case 'alarm-indicator':
      return <AlarmIndicatorRenderer widget={widget} source={source} />;
    case 'sparkline':
      return <SparklineRenderer widget={widget} source={source} />;
    case 'trend':
      return <TrendRenderer widget={widget} source={source} />;
    // HVAC
    case 'schematic-overlay':
      return <SchematicOverlayRenderer widget={widget} source={source} />;
    case 'energy-flow':
      return <EnergyFlowRenderer widget={widget} source={source} />;
    case 'schedule-editor':
      return <ScheduleEditorRenderer widget={widget} source={source} />;
    case 'stage-cascade':
      return <StageCascadeRenderer widget={widget} source={source} />;
    // Compound
    case 'heat-circuit-tile':
      return <HeatCircuitTileRenderer widget={widget} source={source} />;
    case 'heat-pump-kpi':
      return <HeatPumpKpiRenderer widget={widget} source={source} />;
    case 'hot-water-tile':
      return <HotWaterTileRenderer widget={widget} source={source} />;
    case 'room-thermostat':
      return <RoomThermostatRenderer widget={widget} source={source} />;
    case 'pv-self-consumption':
      return <PvSelfConsumptionRenderer widget={widget} source={source} />;
    case 'weather-tile':
      return <WeatherTileRenderer widget={widget} source={source} />;
    // Free design
    case 'action-button':
      return <ActionButtonRenderer widget={widget} source={source} />;
    case 'text':
      return <TextRenderer widget={widget} source={source} />;
    case 'hero-value':
      return <HeroValueRenderer widget={widget} source={source} />;
    case 'image':
      return <ImageRenderer widget={widget} source={source} />;
    case 'shape':
      return <ShapeRenderer widget={widget} source={source} />;
    case 'symbol':
      return <SymbolRenderer widget={widget} source={source} />;
    case 'schematic-part':
      return <SchematicPartRenderer widget={widget} source={source} />;
    case 'nav-bar':
      return <NavBarRenderer widget={widget} source={source} />;
    case 'theme-toggle':
      return <ThemeToggleRenderer widget={widget} source={source} />;
    case 'alarm-list':
      return <AlarmListRenderer widget={widget} source={source} />;
    case 'dial':
      return <DialRenderer widget={widget} source={source} />;
    case 'day-schedule':
      return <DayScheduleRenderer widget={widget} source={source} />;
    case 'heating-curve':
      return <HeatingCurveRenderer widget={widget} source={source} />;
    case 'tank':
      return <TankRenderer widget={widget} source={source} />;
    case 'power-flow':
      return <PowerFlowRenderer widget={widget} source={source} />;
    case 'month-bars':
      return <MonthBarsRenderer widget={widget} source={source} />;
    case 'split-bar':
      return <SplitBarRenderer widget={widget} source={source} />;
    case 'user-invite':
      return <UserInviteRenderer widget={widget} source={source} />;
    default: {
      // TypeScript narrows `widget` to `never` here because the discriminated
      // union is exhausted above — great sign that every kind has a case.
      // Runtime cast keeps a safety-net for future widget kinds that ship in
      // a JSON payload before their renderer + type mirror lands.
      const unknown = widget as WidgetDefinition;
      return (
        <div class="hv-widget-fallback"
             role="alert"
             title={`Kein Renderer für kind='${unknown.kind}' — noch nicht implementiert.`}>
          <strong>{unknown.kind}</strong>
          <span>Renderer offen</span>
        </div>
      );
    }
  }
}

export {
  // Command
  MomentaryButtonRenderer,
  ToggleRenderer,
  EnumSetterRenderer,
  SetpointStepperRenderer,
  SliderRenderer,
  LockGuardRenderer,
  // Display
  GaugeRenderer,
  RollingCounterRenderer,
  StatusLedRenderer,
  ValueTileRenderer,
  AlarmIndicatorRenderer,
  SparklineRenderer,
  TrendRenderer,
  // HVAC
  SchematicOverlayRenderer,
  EnergyFlowRenderer,
  ScheduleEditorRenderer,
  StageCascadeRenderer,
  // Compound
  HeatCircuitTileRenderer,
  HeatPumpKpiRenderer,
  HotWaterTileRenderer,
  RoomThermostatRenderer,
  PvSelfConsumptionRenderer,
  WeatherTileRenderer,
};
